import React, { useEffect, useRef, useState } from 'react';
import { Braces } from 'lucide-react';

// ON A PHONE the rail has no room, so the portal carries its own way around —
// the same two pieces Nucleus uses (frontend/src/components/nucleus/PhoneNav.js):
// a bar along the bottom with every section (Home first), and, on a section's
// page, its sibling pages as a strip under the location line. Above 760px
// neither draws; the rail and the section column do the work.

const PHONE = '(max-width: 760px)';
export function useIsPhone() {
  const read = () => typeof window !== 'undefined' && !!window.matchMedia?.(PHONE).matches;
  const [phone, setPhone] = useState(read);
  useEffect(() => {
    const mq = window.matchMedia?.(PHONE);
    if (!mq) return undefined;
    const on = () => setPhone(mq.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  return phone;
}

/**
 * The bottom bar: Home, then every section's glyph, the one you are behind lit.
 * Eight doors do not fit a 320px phone at the 44px a thumb needs, so the bar
 * scrolls sideways (styles/Frame.css) and keeps the lit door in view.
 */
export function PhoneBar({ sections, active, onHome, onGo }) {
  const bar = useRef(null);
  useEffect(() => {
    const el = bar.current;
    const on = el?.querySelector('.is-on');
    if (!on) return;
    // only as far as it takes: a door already in view leaves the bar where it is
    if (on.offsetLeft < el.scrollLeft) el.scrollLeft = on.offsetLeft;
    else if (on.offsetLeft + on.offsetWidth > el.scrollLeft + el.clientWidth) {
      el.scrollLeft = on.offsetLeft + on.offsetWidth - el.clientWidth;
    }
  }, [active]);
  return (
    <nav className="dev-phonebar" aria-label="Sections" ref={bar}>
      <button type="button" className={`dev-phonebar__door${!active ? ' is-on' : ''}`} onClick={onHome}
        aria-label="Home" aria-current={!active ? 'page' : undefined}>
        <Braces size={18} strokeWidth={1.5} aria-hidden="true" />
      </button>
      {sections.map((s) => {
        const Glyph = s.glyph;
        const on = active === s.id;
        return (
          <button key={s.id} type="button" className={`dev-phonebar__door${on ? ' is-on' : ''}`}
            onClick={() => onGo(s)} aria-label={s.label} title={s.label} aria-current={on ? 'page' : undefined}>
            <Glyph size={18} strokeWidth={1.5} aria-hidden="true" />
          </button>
        );
      })}
    </nav>
  );
}

/** A section's pages, side by side under the location line, the one you are on lit. */
export function PageStrip({ pages, active, onSelect }) {
  const strip = useRef(null);
  // the page you are on is always in view, however far along the strip it sits
  useEffect(() => {
    const on = strip.current?.querySelector('.is-on');
    if (on && strip.current) strip.current.scrollLeft = Math.max(0, on.offsetLeft - 16);
  }, [active]);
  if (!pages || pages.length < 2) return null;
  return (
    <nav className="dev-pagestrip" aria-label="Pages" ref={strip}>
      {pages.map((p) => (
        <button key={p.id} type="button" className={`dev-pagestrip__page${p.id === active ? ' is-on' : ''}`}
          aria-current={p.id === active ? 'page' : undefined} onClick={() => onSelect(p)}>
          {p.label}
        </button>
      ))}
    </nav>
  );
}
